import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, type GridProps } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { type Nil, Text, TextWithTooltip, Utils } from '@pbt/pbt-ui-components'
import type { TextProps } from '@pbt/pbt-ui-components/src/components/Text'

import ClientWarningChip from '~/components/common/ClientWarningChip'
import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import PatientInfoLabel from '~/components/common/labels/PatientInfoLabel'
import Link from '~/components/common/link/Link'
import PatientWarningChip from '~/components/common/PatientWarningChip'
import FeatureToggle from '~/constants/featureToggle'
import useGetPreferredContactMethod from '~/hooks/useGetPreferredContactMethod'
import { fetchClient } from '~/store/actions/clients'
import { fetchDocumentsListV2 } from '~/store/duck/documentsV2'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import { getUser } from '~/store/reducers/users'
import type { TimetableEvent } from '~/types'

import LinkedChewyAccountContainer, {
  KyriosAccountType,
} from '../dashboard/link-chewy-account/LinkedChewyAccountContainer'
import WellnessPlanMemberButton from '../dashboard/wellness-plans/WellnessPlanMemberButton'
import { ClientPreferencesChip } from './ClientPreferencesChip'
import { PatientPreferencesChip } from './PatientPreferencesChip'

const useStyles = makeStyles(
  (theme) => ({
    chip: {
      width: 16,
      height: 16,
      margin: 1,
      alignContent: 'center',
      '&:last-child': {
        marginRight: theme.spacing(0.5),
      },
    },
    wellnessChip: {
      width: 16,
      height: 16,
    },
    button: {
      display: 'flex',
    },
    nameLink: {
      cursor: 'pointer',
    },
  }),
  { name: 'ClientAndPatient' },
)

interface ClientAndPatientProps extends GridProps {
  appointment?: TimetableEvent
  clientId: string | Nil
  loadClient?: boolean
  patientId: string | Nil
  textVariant?: TextProps['variant']
}

/**
 * Displays Client and Patient info in a single line
 * To show client info only, pass patientId as null
 */
const ClientAndPatient = ({
  clientId,
  patientId,
  loadClient = false,
  textVariant = 'lowAccent2',
  appointment,
  ...props
}: ClientAndPatientProps) => {
  const classes = useStyles()

  const client = useSelector(getUser(clientId))
  const patient = useSelector(getPatient(patientId))

  const isSOAPConsentFormsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SOAP_CONSENT_FORMS),
  )

  const dispatch = useDispatch()

  useEffect(() => {
    if (loadClient && clientId) {
      dispatch(fetchClient({ clientId }))
    }
  }, [loadClient, clientId])

  useEffect(() => {
    if (isSOAPConsentFormsEnabled && appointment?.id)
      dispatch(fetchDocumentsListV2({ appointmentId: appointment.id }))
  }, [appointment?.id])

  const preferredPhoneContactMethod = useGetPreferredContactMethod({
    client,
    phoneOnly: true,
  })

  if (!client && !patient) {
    return null
  }

  return (
    <Grid container alignItems="center" flexWrap="nowrap" {...props}>
      <Grid container item flexWrap="nowrap" width="fit-content">
        <ClientWarningChip className={classes.chip} clientId={clientId} />
        <ClientPreferencesChip
          appointment={appointment}
          className={classes.chip}
          clientId={clientId}
        />
        <LinkedChewyAccountContainer
          accountType={KyriosAccountType.CLIENT}
          className={classes.chip}
          clientId={clientId}
        />
        <BusinessShareIcon
          businessIds={client?.clientInContextBusinesses}
          className={classes.chip}
        />
      </Grid>
      <Grid item mr={0.5}>
        <Link
          className={classes.nameLink}
          id="soap-header-client-link"
          target="_blank"
          to={`/client/${clientId}`}
        >
          <TextWithTooltip
            noIcon
            underline
            TooltipProps={{ tooltipPlacement: 'bottom-start' }}
            tooltipText={`${preferredPhoneContactMethod?.value ?? ''}\n${
              client?.email ?? ''
            }`}
            variant={textVariant}
          >
            {Utils.getPersonString(client)}
          </TextWithTooltip>
        </Link>
      </Grid>
      {client && patient && (
        <Grid item mr={0.5}>
          <Text variant={textVariant}>{'>'}</Text>
        </Grid>
      )}
      {patient && (
        <>
          <Grid
            container
            item
            alignItems="center"
            flexWrap="nowrap"
            width="fit-content"
          >
            <PatientWarningChip
              className={classes.chip}
              patientId={patientId}
            />
            <PatientPreferencesChip
              appointment={appointment}
              className={classes.chip}
              patientId={patientId}
            />
            <LinkedChewyAccountContainer
              accountType={KyriosAccountType.PATIENT}
              className={classes.chip}
              clientId={clientId}
              patientId={patientId}
            />
            <WellnessPlanMemberButton
              chip
              readonly
              buttonClassName={classes.chip}
              className={classes.wellnessChip}
              clientId={clientId}
              patientId={patientId}
            />
          </Grid>
          <Grid item mx={0.5}>
            <Link
              className={classes.nameLink}
              id="soap-header-patient-link"
              target="_blank"
              to={`/patient/${patientId}`}
            >
              <Text underline variant={textVariant}>
                {patient?.name}:
              </Text>
            </Link>
          </Grid>
          <PatientInfoLabel patientId={patientId} variant={textVariant} />
        </>
      )}
    </Grid>
  )
}

export default ClientAndPatient
